import React, { createContext, useContext, useState } from 'react';

const UIVersionContext = createContext({
  useModernUI: false,
  toggleUI: () => {},
  uiVersion: 'legacy',
  uiFeatures: {},
  toggleFeature: () => {},
  isFeatureEnabled: () => false,
  previewRole: null,
  setPreviewRole: () => {},
  isPreviewMode: false
});

// Define available UI features - Modern UI is now the default
const DEFAULT_FEATURES = {
  modernNavigation: true, // Top bar & Sidebar - DEFAULT
  modernHomepage: false, // Modern homepage with shadcn components
  modernSystematicTools: false, // Systematic Tools with pagination and cleaner design
  modernOrgChart: false, // Organization Chart with React Flow - DISABLED
  modernTrackers: true, // Enhanced tracker system with improved UI and functionality
  modernTrackerNotes: true, // Unified notes system across all trackers
  modernTrackerFilters: true, // Range and BaseShop filtering for trackers
  // Future features can be added here:
  // modernTheme: false,
  // modernComponents: false,
  // etc.
};

export const UIVersionProvider = ({ children }) => {
  const [uiFeatures, setUIFeatures] = useState(() => {
    // Get features from localStorage or use defaults
    const saved = localStorage.getItem('ui20Features');
    if (saved) {
      try {
        const parsedFeatures = JSON.parse(saved);
        // FORCE modernNavigation to be true (default) - merge with defaults
        const features = { ...DEFAULT_FEATURES, ...parsedFeatures, modernNavigation: true };
        // Save the updated features back to localStorage
        localStorage.setItem('ui20Features', JSON.stringify(features));
        return features;
      } catch (e) {
        console.warn('Failed to parse UI features from localStorage');
      }
    }
    // No saved features - use defaults and save them
    localStorage.setItem('ui20Features', JSON.stringify(DEFAULT_FEATURES));
    return DEFAULT_FEATURES;
  });

  // Preview mode for admins to see different user levels
  const [previewRole, setPreviewRoleState] = useState(() => {
    // Initialize from localStorage adminViewMode
    const savedMode = localStorage.getItem("adminViewMode");
    return savedMode && savedMode !== "Admin" ? savedMode : null;
  });
  //TODO: Determine isPreviewMode based on actual user role
  const isPreviewMode = localStorage.getItem("wb.plan") === "Admin";//previewRole !== null;

  // Wrapper function to sync with localStorage and dispatch event
  const setPreviewRole = (role) => {
    setPreviewRoleState(role);
    
    // Sync with localStorage
    if (role) {
      localStorage.setItem("adminViewMode", role);
    } else {
      localStorage.setItem("adminViewMode", "Admin");
    }
    
    // Dispatch event for useAccountType hook
    window.dispatchEvent(new CustomEvent("viewModeChanged", { detail: role || "Admin" }));
  };

  // Backward compatibility: useModernUI based on modernNavigation
  const useModernUI = uiFeatures.modernNavigation;

  const toggleFeature = (featureName) => {
    setUIFeatures(prev => {
      const newFeatures = {
        ...prev,
        [featureName]: !prev[featureName]
      };
      localStorage.setItem('ui20Features', JSON.stringify(newFeatures));
      return newFeatures;
    });
  };

  const toggleUI = () => {
    // Backward compatibility function
    toggleFeature('modernNavigation');
  };

  const isFeatureEnabled = (featureName) => {
    return uiFeatures[featureName] || false;
  };

  const uiVersion = useModernUI ? 'modern' : 'legacy';

  const value = {
    useModernUI,
    toggleUI,
    uiVersion,
    uiFeatures,
    toggleFeature,
    isFeatureEnabled,
    previewRole,
    setPreviewRole,
    isPreviewMode
  };

  return (
    <UIVersionContext.Provider value={value}>
      {children}
    </UIVersionContext.Provider>
  );
};

export const useUIVersion = () => {
  const context = useContext(UIVersionContext);
  if (!context) {
    throw new Error('useUIVersion must be used within a UIVersionProvider');
  }
  return context;
};